import { isLoginFlowSpaPath } from './isLoginFlowSpaPath';
import { shouldRedirectToIdpUrlOnLogin } from './shouldRedirectToIdpUrlOnLogin';

const REQUEST_TIMEOUT_MS = 5000;

async function getJson(url: string): Promise<{ status: number; body: unknown } | null> {
    try {
        const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) });
        const text = await response.text();
        return { status: response.status, body: text ? JSON.parse(text) : undefined };
    } catch {
        return null;
    }
}

/**
 * Resolves to the IDP login URL to redirect to, or undefined if the app should boot normally.
 *
 * Called from index.tsx before the dynamic `import('./bootstrap')` that pulls in the rest of the
 * app (React, MUI, all feature modules): as long as that import never runs, the browser never
 * downloads that bundle, since a dynamic import is invisible to the HTML preload scanner - unlike
 * a <script> tag declared in markup, there's nothing here to race against.
 *
 * Uses `/api/controlpanel/v1/public-app-settings` rather than the feature flags endpoint - the
 * latter requires an authenticated session (`AuthorizeByRoleAttribute` denies requests with no
 * session, even with `Authorize = Any`), so it always 401s for the unauthenticated users this
 * check exists to handle. public-app-settings is genuinely anonymous ([AllowAnonymous]) and
 * already computes the combined flag + auth-type decision server-side.
 *
 * Skipped on the login-flow SPA pages (`isLoginFlowSpaPath`): those pages are reached only after
 * the IDP challenge already succeeded, before position/incident is committed, so `/session`
 * legitimately 401s there. Without this guard, that 401 gets read as "unauthenticated" and bounces
 * back to `/login/login/`, which round-trips through `/login/callback/` straight back to the same
 * page - an infinite loop.
 */
export async function checkShouldRedirectToIdpUrlOnLogin(siteUrl: string, pathname: string): Promise<string | undefined> {
    if (isLoginFlowSpaPath(pathname)) {
        return undefined;
    }

    const [appSettingsResult, sessionResult] = await Promise.all([
        getJson(`${siteUrl}/api/controlpanel/v1/public-app-settings`),
        getJson(`${siteUrl}/api/controlpanel/v1/session`),
    ]);
    if (appSettingsResult?.status !== 200 || !appSettingsResult.body) {
        return undefined;
    }

    const redirectToIdpUrlOnLogin = Boolean((appSettingsResult.body as { redirectToIdpUrlOnLogin?: boolean }).redirectToIdpUrlOnLogin);
    const hasValidSession = sessionResult?.status === 200;

    return shouldRedirectToIdpUrlOnLogin({ redirectToIdpUrlOnLogin, hasValidSession }) ? `${siteUrl}/login/login/` : undefined;
}
